import apiClient from './apiClient';
import { handleApiError, formatApiResponse } from './api';

/**
 * Servicio para el Dashboard Principal (HOME)
 * Basado en la API documentada en home_module.md
 */
const dashboardService = {
  // ==================== DASHBOARD PRINCIPAL ====================

  /**
   * Obtener dashboard completo con todas las métricas
   * @param {Object} params - Parámetros de consulta
   * @param {string} params.period - Período: day, week, month, quarter, year
   */
  getDashboard: async (params = {}) => {
    try {
      const response = await apiClient.get('/reports/dashboard', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Obtener métricas rápidas (Quick Stats)
   * @param {Object} params - Parámetros de consulta
   * @param {string} params.period - Período: today, week, month, quarter, year
   */
  getQuickStats: async (params = {}) => {
    try {
      const response = await apiClient.get('/reports/dashboard/quick-stats', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // ==================== TOP RANKINGS ====================

  /**
   * Obtener Top 20 clientes que más compran
   * @param {Object} params - Parámetros de consulta
   * @param {string} params.period - Período: week, month, quarter, year
   * @param {number} params.limit - Límite de resultados (1-100, default: 20)
   */
  getTopClients: async (params = {}) => {
    try {
      const response = await apiClient.get('/reports/dashboard/top-clients', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Obtener Top 20 productos más vendidos
   * @param {Object} params - Parámetros de consulta
   * @param {string} params.period - Período: week, month, quarter, year
   * @param {number} params.limit - Límite de resultados (1-100, default: 20)
   * @param {string} params.department - Departamento: betel, naturalandlab, other_brands
   */
  getTopProducts: async (params = {}) => {
    try {
      const response = await apiClient.get('/reports/dashboard/top-products', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // ==================== RESUMEN FINANCIERO ====================

  /**
   * Obtener resumen financiero: facturas pendientes, vencidas y órdenes
   */
  getFinancialOverview: async () => {
    try {
      const response = await apiClient.get('/reports/dashboard/financial-overview');
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // ==================== GRÁFICAS Y REPORTES ====================

  /**
   * Obtener ventas por año para gráficas
   * @param {Object} params - Parámetros de consulta
   * @param {number} params.from_year - Año inicial
   * @param {number} params.to_year - Año final
   * @param {boolean} params.include_projections - Incluir proyecciones
   */
  getSalesByYear: async (params = {}) => {
    try {
      const response = await apiClient.get('/reports/dashboard/sales-by-year', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Obtener ventas por mes para gráficas
   * @param {Object} params - Parámetros de consulta
   * @param {number} params.year - Año a consultar (default: año actual)
   * @param {boolean} params.compare_previous_year - Comparar con año anterior (default: true)
   */
  getSalesByMonth: async (params = {}) => {
    try {
      const response = await apiClient.get('/reports/dashboard/sales-by-month', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // ==================== UTILIDADES ====================

  /**
   * Períodos disponibles para análisis
   */
  getPeriods: () => {
    return [
      { value: 'day', label: 'Día' },
      { value: 'week', label: 'Semana' },
      { value: 'month', label: 'Mes' },
      { value: 'quarter', label: 'Trimestre' },
      { value: 'year', label: 'Año' }
    ];
  },

  /**
   * Períodos para Top 20
   */
  getTopPeriods: () => {
    return [
      { value: 'week', label: 'Semana' },
      { value: 'month', label: 'Mes' },
      { value: 'quarter', label: 'Trimestre' },
      { value: 'year', label: 'Año' }
    ];
  },

  /**
   * Tipos de tabs disponibles
   */
  getTabTypes: () => {
    return [
      { value: 'clientes', label: 'CLIENTES' },
      { value: 'productos', label: 'PRODUCTOS' }
    ];
  },

  /**
   * Obtener color para gráfica por índice
   */
  getChartColor: (index) => {
    const colors = [
      '#FF6B9D', // Rosa/Magenta
      '#C44569', // Rojo oscuro
      '#FFA502', // Naranja
      '#F8B500', // Amarillo
      '#F6B93B', // Dorado
      '#A29BFE', // Púrpura
      '#6C5CE7', // Violeta
      '#00B894', // Verde
      '#55E6C1', // Turquesa
      '#00D2D3', // Cyan
      '#1DD1A1', // Verde agua
      '#54A0FF', // Azul
      '#5F27CD', // Morado
      '#FF9FF3', // Rosa claro
      '#48DBFB', // Azul claro
      '#0ABDE3', // Azul intenso
      '#10AC84', // Verde esmeralda
      '#EE5A6F', // Rojo coral
      '#F368E0', // Fucsia
      '#FC427B'  // Rosa intenso
    ];
    return colors[index % colors.length];
  },

  /**
   * Formatear moneda
   */
  formatCurrency: (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  },

  /**
   * Formatear porcentaje
   */
  formatPercentage: (value) => {
    return `${value.toFixed(1)}%`;
  },

  /**
   * Calcular porcentaje del total
   */
  calculatePercentage: (value, total) => {
    if (total === 0) return 0;
    return (value / total) * 100;
  }
};

export default dashboardService;
